import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import Switch from "@mui/material/Switch";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import BasicLayout from "layouts/authentication/components/BasicLayout";
import { GoogleLogin } from "@react-oauth/google"; // Import GoogleLogin from @react-oauth/google
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

import bgImage from "assets/images/bg-sign-in-basic.jpeg";

// Create an Alert component for Snackbar
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function Basic() {
  const [rememberMe, setRememberMe] = useState(false);
  const [user_email, setEmail] = useState("");
  const [user_password, setPassword] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const navigate = useNavigate();

  const handleSetRememberMe = () => setRememberMe(!rememberMe);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const enableNormalLogin = process.env.REACT_APP_ENABLE_NORMAL_LOGIN === "true";
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (token) {
      navigate("/dashboard", { replace: true });
    }
  }, []);

  const fetchPermissionsFunctions = async (userId) => {
    try {
      const permissionsRes = await axios.get(`${API_BASE_URL}/api/rbac/user/${userId}/permissions`);
      const functionsRes = await axios.get(`${API_BASE_URL}/api/rbac/user/${userId}/modules`);
      sessionStorage.setItem("rolepermissions", JSON.stringify(permissionsRes.data.permissions));
      sessionStorage.setItem("modulefunctions", JSON.stringify(functionsRes.data.modules));
    } catch (error) {
      console.error("Failed to fetch permissions or functions:", error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    let email = user_email;
    let password = user_password;
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
        email: email,
        password: password,
      });

      if (response.data.success) {
        const user = response.data.user;
        sessionStorage.setItem("token", response.data.token);
        sessionStorage.setItem("user", JSON.stringify(response.data.user));
        await fetchPermissionsFunctions(user.user_id);
        navigate("/dashboard");
        setSnackbarMessage("Login successful!");
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
      } else {
        setSnackbarMessage("Invalid credentials!..");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      }
    } catch (error) {
      setSnackbarMessage("Normal login failed!");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  const handleGoogleLogin = async (response) => {
    // Send the Google token to your backend to authenticate
    const googleToken = response.credential;
    try {
      const backendResponse = await axios.post(`${API_BASE_URL}/api/auth/google-login`, {
        credential: googleToken,
      });
      if (backendResponse.data.token) {
        const user = backendResponse.data.user;
        sessionStorage.setItem("token", backendResponse.data.token);
        sessionStorage.setItem("user", JSON.stringify(backendResponse.data.user));
        await fetchPermissionsFunctions(user.user_id);
        navigate("/dashboard");
        setSnackbarMessage("Google login successful!");
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
      } else {
        setSnackbarMessage("Google login failed!");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      }
    } catch (error) {
      setSnackbarMessage("Google login failed!");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  return (
    <BasicLayout image={bgImage}>
      <Card>
        {/* Header */}
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Sign In
          </MDTypography>
        </MDBox>

        {/* --- Google Login Section --- */}
        <MDBox mt={3} display="flex" flexDirection="column" alignItems="center" px={3}>
          <Card
            sx={{
              p: 2,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #9abaeaff 0%, #a7c3e0ff 100%)",
              boxShadow: "0 3px 10px rgba(0, 0, 0, 0.1)",
              transition: "all 0.3s ease",
              "&:hover": { transform: "scale(1.03)" },
            }}
          >
            <div style={{ transform: "scale(1.0)", transformOrigin: "center" }}>
              <GoogleLogin
                onSuccess={handleGoogleLogin}
                onError={() => alert("Google login failed!")}
                width="250"
              />
            </div>
          </Card>
          {enableNormalLogin && (
            <MDTypography variant="button" color="text" sx={{ mt: 2 }}>
              OR
            </MDTypography>
          )}
        </MDBox>

        {/* --- Normal Login Section --- */}
        {enableNormalLogin && (
          <MDBox pt={3} pb={3} px={3}>
            <MDBox component="form" role="form" onSubmit={handleLogin}>
              <MDBox mb={2}>
                <MDInput
                  type="email"
                  label="Email"
                  fullWidth
                  value={user_email}
                  onChange={(e) => setEmail(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </MDBox>

              <MDBox mb={2}>
                <MDInput
                  type="password"
                  label="Password"
                  fullWidth
                  value={user_password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </MDBox>

              <MDBox display="flex" alignItems="center" ml={-1}>
                <Switch checked={rememberMe} onChange={handleSetRememberMe} />
                <MDTypography
                  variant="button"
                  fontWeight="regular"
                  color="text"
                  onClick={handleSetRememberMe}
                  sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
                >
                  &nbsp;&nbsp;Remember me
                </MDTypography>
              </MDBox>

              <MDBox mt={4} mb={1}>
                <MDButton variant="gradient" color="info" fullWidth type="submit">
                  Sign In
                </MDButton>
              </MDBox>
            </MDBox>
          </MDBox>
        )}
      </Card>

      {/* --- Snackbar --- */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </BasicLayout>
  );
}

export default Basic;
